import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Typography, CircularProgress, Tooltip, IconButton, useTheme, useMediaQuery } from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import { CustomTable } from 'components'
import useRouter from 'utils/useRouter'
import useStyles from './DashboardTableSection.styles'

export const DashboardTableSection = ({ cell, tableData, isProcessing, onConfigClick }) => {
  const classes = useStyles()
  const theme = useTheme()
  const router = useRouter()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  // Map readable table header title
  const getTableTitle = () => {
    if (!cell) return 'Carregando Tabela...'
    switch (cell.type) {
      case 'ultimos_cadaveres':
        return 'Últimos Cadáveres Cadastrados'
      case 'ultimos_exames_atribuidos':
        return 'Últimos Exames Atribuídos a Mim'
      case 'ultimos_exames_solicitados':
        return 'Últimos Exames que Solicitei'
      default:
        return 'Listagem de Atividades'
    }
  }

  const handleItemClick = (row) => {
    if (!row || !row.id) return
    const activeType = cell?.type

    if (activeType === 'ultimos_exames_atribuidos' || activeType === 'ultimos_exames_solicitados') {
      router.history.push(`/casos/caso/solicitacao/exameSolicitado/detalhar/${row.id}`)
    } else {
      // default: ultimos_cadaveres
      router.history.push(`/casos/caso/cadaver/detalhar/${row.id}`)
    }
  }

  const renderCellWithTooltip = (text, maxWidth = 120) => (
    <Tooltip title={text || ''} enterDelay={300} arrow>
      <div
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: maxWidth,
        }}
      >
        {text}
      </div>
    </Tooltip>
  )

  const renderStatusBadge = (status) => {
    const isFinalizado = status === 'Finalizado' || status === 'Publicado' || status === 'Liberado'
    const isPendente = status === 'Pendente' || status === 'Aguardando liberação'
    return (
      <span
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          backgroundColor: isFinalizado ? 'rgba(16, 185, 129, 0.15)' : isPendente ? 'rgba(244, 103, 44, 0.15)' : 'rgba(2, 132, 199, 0.15)',
          color: isFinalizado ? '#10B981' : isPendente ? '#F4672C' : '#0284C7',
        }}
      >
        {status}
      </span>
    )
  }

  const renderMobileCard = (row) => {
    const activeType = cell?.type

    if (activeType === 'ultimos_exames_atribuidos' || activeType === 'ultimos_exames_solicitados') {
      return (
        <div key={row.id || row.identificador} className={classes.mobileCard} onClick={() => handleItemClick(row)}>
          <div className={classes.mobileCardHeader}>
            <Typography className={classes.mobileCardId}>{row.identificador}</Typography>
            {renderStatusBadge(row.status)}
          </div>
          <Typography className={classes.mobileCardTitle} variant="body1">
            {row.tipo}
          </Typography>
          <div className={classes.mobileCardFooter}>
            <Typography className={classes.mobileCardDate} variant="caption">
              {row.data}
            </Typography>
          </div>
        </div>
      )
    }

    // default: ultimos_cadaveres
    return (
      <div key={row.id || row.nic} className={classes.mobileCard} onClick={() => handleItemClick(row)}>
        <div className={classes.mobileCardHeader}>
          <Typography className={classes.mobileCardId}>{row.nic}</Typography>
          <Typography className={classes.mobileCardDate} variant="caption">
            {row.data}
          </Typography>
        </div>
        <Typography className={classes.mobileCardTitle} variant="body1">
          {row.nome}
        </Typography>
        {row.necroterio && (
          <Typography className={classes.mobileCardSub} variant="body2">
            {row.necroterio}
          </Typography>
        )}
      </div>
    )
  }

  const tableColumns = useMemo(() => {
    const activeType = cell?.type

    if (activeType === 'ultimos_exames_atribuidos') {
      return [
        { title: 'Identificador', field: 'identificador', cellStyle: { fontWeight: 600 } },
        {
          title: 'Tipo do Exame',
          field: 'tipo',
          render: (row) => renderCellWithTooltip(row.tipo, 150),
        },
        {
          title: 'Status',
          field: 'status',
          render: (row) => renderStatusBadge(row.status),
        },
        { title: 'Data', field: 'data' },
      ]
    }

    if (activeType === 'ultimos_exames_solicitados') {
      return [
        { title: 'Identificador', field: 'identificador', cellStyle: { fontWeight: 600 } },
        {
          title: 'Tipo do Exame',
          field: 'tipo',
          render: (row) => renderCellWithTooltip(row.tipo, 150),
        },
        {
          title: 'Status',
          field: 'status',
          render: (row) => renderStatusBadge(row.status),
        },
        { title: 'Data', field: 'data' },
      ]
    }

    // default: ultimos_cadaveres
    return [
      { title: 'NIC', field: 'nic', cellStyle: { fontWeight: 600 } },
      {
        title: 'Necrotério',
        field: 'necroterio',
        render: (row) => renderCellWithTooltip(row.necroterio, 150),
      },
      {
        title: 'Nome',
        field: 'nome',
        render: (row) => renderCellWithTooltip(row.nome, 150),
      },
      { title: 'Data', field: 'data' },
    ]
  }, [cell?.type])

  return (
    <div className={classes.dashboardCell}>
      <Tooltip title="Configurar Tabela">
        <IconButton className={classes.configButton} onClick={onConfigClick}>
          <SettingsIcon style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <div className={classes.tableCard}>
        {isProcessing && (
          <div className={classes.cellLoadingOverlay}>
            <CircularProgress size={24} disableShrink />
          </div>
        )}

        <Typography className={classes.tableTitle} variant="h3">
          {getTableTitle()}
        </Typography>
        <Typography className={classes.tableSubtitle} variant="body2">
          Últimos registros operacionais cadastrados no banco de dados do SIGEP
        </Typography>

        {isMobile ? (
          !tableData || tableData.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary">
                Nenhum registro encontrado
              </Typography>
            </div>
          ) : (
            <div className={classes.mobileCardsContainer}>{tableData.map(renderMobileCard)}</div>
          )
        ) : (
          <CustomTable
            columns={tableColumns}
            data={tableData || []}
            onRowClick={(event, rowData) => handleItemClick(rowData)}
            options={{
              search: false,
              paging: false,
              elevation: 0,
              showTitle: false,
              toolbar: false,
            }}
          />
        )}
      </div>
    </div>
  )
}

DashboardTableSection.propTypes = {
  cell: PropTypes.object,
  tableData: PropTypes.array,
  isProcessing: PropTypes.bool,
  onConfigClick: PropTypes.func.isRequired,
}
